import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db, ref, onValue, get } from "./firebase";
import { useAuth } from "./AuthContext";
import { FaBook, FaBell } from "react-icons/fa";
import { formatCourseId } from "./utils/formatCourse";

// Date formatting helper
const formatDate = (date, options = {}) => {
  if (!date) return '';
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

const Dashboard = () => {
  const [username, setUsername] = useState("Loading...");
  const { user } = useAuth();
  const [courseInfo, setCourseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [courseProgress, setCourseProgress] = useState(0);

  // State for dynamic data
  const [upcomingAssignments, setUpcomingAssignments] = useState([]);
  const [latestChapterDone, setLatestChapterDone] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [latestAnnouncement, setLatestAnnouncement] = useState(null);

  const contactNumber = "+201004672283"; // WhatsApp contact number
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      try {
        setLoading(true);
        const email = user.email;
        let foundUser = null;
        let courseName = null;
        let unsubscribeFunctions = [];

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            courseName = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  courseName = courseId;
                  break;
                }
              }
            }
          }
        }

        if (foundUser) {
          setUsername(foundUser.name);
          setEnrolledCourse(courseName);
        }

        // If we found a course, fetch its data
        if (courseName) {
          const courseRef = ref(db, `courses/${courseName}`);
          const unsubscribeCourse = onValue(courseRef, (snapshot) => {
            if (snapshot.exists()) {
              setCourseInfo(snapshot.val());
            }
          });
          unsubscribeFunctions.push(unsubscribeCourse);

          // Fetch chapters progress
          const chaptersRef = ref(db, `courses/${courseName}/chapters`);
          const unsubscribeChapters = onValue(chaptersRef, (snapshot) => {
            if (snapshot.exists()) {
              const chaptersData = snapshot.val();
              const completedChapters = Object.values(chaptersData).filter((ch) => ch.done).length;
              const totalChapters = Object.keys(chaptersData).length;
              const calculatedProgress = Math.round((completedChapters / totalChapters) * 100);
              setCourseProgress(calculatedProgress);

              // Find the latest completed chapter
              const completedChaptersList = Object.values(chaptersData)
                .filter((ch) => ch.done)
                .map(ch => ({
                  ...ch,
                  completedAt: ch.completedAt ? new Date(ch.completedAt) : new Date(0)
                }))
                .sort((a, b) => b.completedAt - a.completedAt);

              setLatestChapterDone(completedChaptersList[0] || null);
            }
          });
          unsubscribeFunctions.push(unsubscribeChapters);

          // Fetch assignments
          const assignmentsRef = ref(db, `courses/${courseName}/homeworks`);
          const unsubscribeAssignments = onValue(assignmentsRef, (snapshot) => {
            if (snapshot.exists()) {
              const assignmentsData = Object.entries(snapshot.val()).map(([id, hw]) => ({
                id,
                ...hw,
                dueDateObj: new Date(hw.dueDate)
              }));

              const today = new Date();
              const upcoming = assignmentsData
                .filter(assignment => assignment.dueDateObj > today)
                .sort((a, b) => a.dueDateObj - b.dueDateObj)
                .slice(0, 3);

              setUpcomingAssignments(upcoming);
            }
          });
          unsubscribeFunctions.push(unsubscribeAssignments);

          // Fetch meetings
          const meetingsRef = ref(db, `courses/${courseName}/meetings`);
          const unsubscribeMeetings = onValue(meetingsRef, (snapshot) => {
            if (snapshot.exists()) {
              const meetingsData = Object.entries(snapshot.val()).map(([id, meeting]) => ({
                id,
                ...meeting,
                dateObj: new Date(meeting.date)
              }));

              const today = new Date();
              const upcoming = meetingsData
                .filter(meeting => meeting.dateObj > today)
                .sort((a, b) => a.dateObj - b.dateObj)
                .slice(0, 2);

              setUpcomingMeetings(upcoming);
            }
          });
          unsubscribeFunctions.push(unsubscribeMeetings);
        }

        // Fetch latest announcement
        if (courseName) {
          const announcementsRef = ref(db, `courses/${courseName}/announcements`);
          const unsubscribeAnnouncements = onValue(announcementsRef, (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val();
              const announcementsList = Object.entries(data).map(([id, announcement]) => ({
                id,
                ...announcement,
                date: new Date(announcement.date)
              })).sort((a, b) => b.date - a.date);
              setLatestAnnouncement(announcementsList[0] || null);
            } else {
              setLatestAnnouncement(null);
            }
          });
          unsubscribeFunctions.push(unsubscribeAnnouncements);
        }

        return () => {
          // Call all unsubscribe functions
          unsubscribeFunctions.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
              unsubscribe();
            }
          });
        };
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!enrolledCourse) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-white">
        <h2 className="text-2xl font-bold mb-4">No Course Found</h2>
        <p className="text-gray-400 mb-6">You are not enrolled in any course yet.</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/complete-registration"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Complete Registration
          </Link>
          <Link
            to="/login"
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
          >
            Return to Login
          </Link>
        </div>
      </div>
    );
  }

  // Check if user has pending registration
  if (enrolledCourse === "pending_registration") {
    // Redirect to complete registration page
    navigate("/complete-registration", { replace: true });
    return null;
  }

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Hero Section */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-teal-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                Welcome back, {username}!
              </h1>
              <p className="text-teal-100 text-base md:text-lg mt-1">
                {enrolledCourse ? `Course: ${formatCourseId(enrolledCourse)}` : "No course enrolled"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate('/dashboard/videos')}
                className="flex-1 md:flex-none px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 text-sm md:text-base"
              >
                <FaBook className="text-lg" />
                <span>Continue Learning</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/announcements')}
                className="flex-1 md:flex-none px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 text-sm md:text-base"
              >
                <FaBell className="text-lg" />
                <span>Announcements</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Syllabus Progress */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Syllabus Progress</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm text-gray-400">
              <span>Overall Progress</span>
              <span>{courseProgress}%</span>
            </div>
            <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-teal-500 to-sky-500 rounded-full transition-all duration-500"
                style={{ width: `${courseProgress}%` }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-gray-700/30 rounded-lg p-3">
                <div className="text-sm text-gray-400 mb-1">Completed</div>
                <div className="text-xl font-semibold text-white">
                  {courseInfo?.chapters ? Object.values(courseInfo.chapters).filter(ch => ch.done).length : 0}
                </div>
              </div>
              <div className="bg-gray-700/30 rounded-lg p-3">
                <div className="text-sm text-gray-400 mb-1">Total</div>
                <div className="text-xl font-semibold text-white">
                  {courseInfo?.chapters ? Object.keys(courseInfo.chapters).length : 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Latest Announcement */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Latest Announcement</h2>
          {latestAnnouncement ? (
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-white">{latestAnnouncement.title}</h3>
              <p className="text-gray-400 text-sm">{latestAnnouncement.content}</p>
              <div className="text-xs text-gray-500">
                {formatDate(latestAnnouncement.createdAt)}
              </div>
            </div>
          ) : (
            <p className="text-gray-400">No announcements yet</p>
          )}
        </div>
      </div>

      {/* Upcoming Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Assignments */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Upcoming Assignments</h2>
          {upcomingAssignments.length > 0 ? (
            <div className="space-y-4">
              {upcomingAssignments.map((assignment) => (
                <div key={assignment.id} className="p-4 bg-gray-700/30 rounded-lg">
                  <h3 className="font-medium text-white">{assignment.title}</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Due: {formatDate(assignment.dueDateObj, { year: undefined })}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">No upcoming assignments</p>
          )}
        </div>

        {/* Latest Chapter Completed */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Latest Chapter Completed</h2>
          {latestChapterDone ? (
            <div className="p-4 bg-gray-700/30 rounded-lg">
              <h3 className="font-medium text-white">{latestChapterDone.title || "Untitled Chapter"}</h3>
              <p className="text-sm text-gray-400 mt-1">Completed on: {formatDate(latestChapterDone.completedAt)}</p>
            </div>
          ) : (
            <p className="text-gray-400">No chapters completed yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;